import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Building2, Mail } from "lucide-react";
import { AxiosError } from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  PasswordInput,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";

interface FieldErrors {
  email?: string[];
  password?: string[];
}

export function LoginPage() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  if (isLoading) return null;
  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    setGeneralError(null);

    try {
      const user = await login({ email, password });
      // If the server says password change is required, AuthProvider routes there.
      // Otherwise land on the admin dashboard.
      if (!user.must_change_password) {
        const from = (location.state as { from?: string } | null)?.from ?? "/";
        navigate(from, { replace: true });
      } else {
        navigate("/force-password-change", { replace: true });
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as { errors?: FieldErrors; message?: string };
        setErrors(data.errors ?? {});
        if (!data.errors) setGeneralError(data.message ?? "Login failed");
      } else {
        setGeneralError("Unable to sign in. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-linear-to-br from-primary via-primary/95 to-slate-900/60" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl mb-4 shadow-2xl border border-white/20">
            <img src="/logo.png" alt="Lekki Serviced Flats" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">Admin Portal</h1>
          <p className="text-white/90 text-lg">Estate management dashboard</p>
        </div>

        <Card className="shadow-2xl border-white/20 backdrop-blur-sm bg-white/95">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold">Administrator Sign In</CardTitle>
            <CardDescription className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Lekki Serviced Flats
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {generalError && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {generalError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-sm font-semibold">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <Input
                    id="admin-email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="admin@lekkiservicedflats.com"
                    className="pl-11 h-11 bg-secondary/50 border-border focus:border-accent transition-colors"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!errors.email}
                  />
                </div>
                {errors.email?.map((msg) => (
                  <p key={msg} className="text-xs text-destructive">
                    {msg}
                  </p>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-sm font-semibold">
                  Password
                </Label>
                <PasswordInput
                  id="admin-password"
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                  className="h-11 bg-secondary/50 border-border focus:border-accent transition-colors"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!errors.password}
                />
                {errors.password?.map((msg) => (
                  <p key={msg} className="text-xs text-destructive">
                    {msg}
                  </p>
                ))}
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-linear-to-r from-accent to-emerald-600 hover:from-accent/90 hover:to-emerald-500 shadow-lg text-base font-semibold"
              >
                {submitting ? "Signing in…" : "Sign In as Admin"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
            <p className="text-white/90 text-sm font-medium">© 2026 LSF Estate Management</p>
          </div>
        </div>
      </div>
    </div>
  );
}
